import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo } from 'react';
import jalaali from 'jalaali-js';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { toPersianDigits } from '@/lib/jalali';
import clsx from 'clsx';
const PERSIAN_MONTHS = [
    'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
    'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند',
];
// Jalali week starts on Saturday.
const PERSIAN_WEEKDAYS_SHORT = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
const parseJalali = (s) => {
    if (s) {
        const [jy, jm, jd] = s.split('-').map(Number);
        if (jy && jm && jd)
            return { jy, jm, jd };
    }
    const today = new Date();
    return jalaali.toJalaali(today.getFullYear(), today.getMonth() + 1, today.getDate());
};
const formatJalali = (jy, jm, jd) => `${jy}-${String(jm).padStart(2, '0')}-${String(jd).padStart(2, '0')}`;
const jalaliDayOfWeek = (jy, jm, jd) => {
    // Returns 0=Saturday … 6=Friday (Persian week order).
    const { gy, gm, gd } = jalaali.toGregorian(jy, jm, jd);
    const dow = new Date(Date.UTC(gy, gm - 1, gd)).getUTCDay(); // 0=Sun … 6=Sat
    return (dow + 1) % 7;
};
export function JalaliCalendar({ value, onChange, disablePast, maxAdvanceDays, disableFridays, availableDates, markedDates, className, }) {
    const availableSet = useMemo(() => availableDates ? new Set(availableDates) : null, [availableDates]);
    const markedSet = useMemo(() => new Set(markedDates ?? []), [markedDates]);
    const todayJ = useMemo(() => {
        const n = new Date();
        return jalaali.toJalaali(n.getFullYear(), n.getMonth() + 1, n.getDate());
    }, []);
    // The month/year currently displayed (independent from the selected value).
    const initial = parseJalali(value);
    const [view, setView] = useState({ jy: initial.jy, jm: initial.jm });
    // When the external `value` changes, keep the visible month in sync.
    useEffect(() => {
        const p = parseJalali(value);
        setView({ jy: p.jy, jm: p.jm });
    }, [value]);
    const monthLength = jalaali.jalaaliMonthLength(view.jy, view.jm);
    const firstDow = jalaliDayOfWeek(view.jy, view.jm, 1);
    // Cells = leading blanks + days, padded to a multiple of 7.
    const cells = [];
    for (let i = 0; i < firstDow; i++)
        cells.push(null);
    for (let d = 1; d <= monthLength; d++)
        cells.push({ jd: d });
    while (cells.length % 7 !== 0)
        cells.push(null);
    const goto = (deltaMonths) => {
        let jm = view.jm + deltaMonths;
        let jy = view.jy;
        while (jm < 1) {
            jm += 12;
            jy -= 1;
        }
        while (jm > 12) {
            jm -= 12;
            jy += 1;
        }
        setView({ jy, jm });
    };
    // Compute the upper bound as a Jalali date (inclusive) if maxAdvanceDays set.
    const maxDate = useMemo(() => {
        if (!maxAdvanceDays)
            return null;
        const t = new Date();
        t.setDate(t.getDate() + maxAdvanceDays);
        return jalaali.toJalaali(t.getFullYear(), t.getMonth() + 1, t.getDate());
    }, [maxAdvanceDays]);
    const isDisabled = (jy, jm, jd) => {
        if (disableFridays && jalaliDayOfWeek(jy, jm, jd) === 6)
            return true;
        if (disablePast) {
            const t = todayJ;
            if (jy < t.jy || (jy === t.jy && jm < t.jm) || (jy === t.jy && jm === t.jm && jd < t.jd))
                return true;
        }
        if (maxDate) {
            const m = maxDate;
            if (jy > m.jy || (jy === m.jy && jm > m.jm) || (jy === m.jy && jm === m.jm && jd > m.jd))
                return true;
        }
        if (availableSet) {
            const key = formatJalali(jy, jm, jd);
            if (!availableSet.has(key))
                return true;
        }
        return false;
    };
    const isMarked = (jy, jm, jd) => markedSet.has(formatJalali(jy, jm, jd));
    // True if there's a menu on this day (in the availableSet).
    // When no availableSet is supplied, every day is "available" in this sense.
    const isAvailable = (jy, jm, jd) => !availableSet || availableSet.has(formatJalali(jy, jm, jd));
    // Find the next month (forward search up to 24 months) that has at least one
    // selectable day. Returns null if nothing in range.
    const findNextMonthWithAvailable = (deltaSign) => {
        if (!availableSet || availableSet.size === 0)
            return null;
        let jy = view.jy;
        let jm = view.jm;
        for (let i = 0; i < 24; i++) {
            jm += deltaSign;
            while (jm < 1) {
                jm += 12;
                jy -= 1;
            }
            while (jm > 12) {
                jm -= 12;
                jy += 1;
            }
            // Any date in the (jy, jm) month present in availableSet?
            const prefix = `${jy}-${String(jm).padStart(2, '0')}-`;
            for (const d of availableSet) {
                if (d.startsWith(prefix) && !isDisabled(jy, jm, Number(d.slice(prefix.length)))) {
                    return { jy, jm };
                }
            }
        }
        return null;
    };
    // Does the currently-displayed month have any selectable date?
    const currentMonthHasAvailable = (() => {
        if (!availableSet)
            return true;
        const prefix = `${view.jy}-${String(view.jm).padStart(2, '0')}-`;
        for (const d of availableSet) {
            if (d.startsWith(prefix)) {
                const jd = Number(d.slice(prefix.length));
                if (!isDisabled(view.jy, view.jm, jd))
                    return true;
            }
        }
        return false;
    })();
    const sel = value ? parseJalali(value) : null;
    return (_jsxs("div", { className: clsx('w-72 select-none rounded-2xl border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-900', className), children: [_jsxs("div", { className: "mb-2 flex items-center justify-between", children: [_jsx("button", { type: "button", onClick: () => goto(-1), className: "rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800", "aria-label": "\u0645\u0627\u0647 \u0642\u0628\u0644", children: _jsx(ChevronRightIcon, { className: "h-4 w-4" }) }), _jsxs("div", { className: "flex items-center gap-1 text-sm font-semibold", children: [_jsx("span", { children: PERSIAN_MONTHS[view.jm - 1] }), _jsx("span", { className: "text-gray-500", children: toPersianDigits(view.jy) })] }), _jsx("button", { type: "button", onClick: () => goto(1), className: "rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800", "aria-label": "\u0645\u0627\u0647 \u0628\u0639\u062F", children: _jsx(ChevronLeftIcon, { className: "h-4 w-4" }) })] }), _jsx("div", { className: "mb-1 grid grid-cols-7 text-center text-[11px] font-medium text-gray-500", children: PERSIAN_WEEKDAYS_SHORT.map((d) => _jsx("div", { className: "py-1", children: d }, d)) }), _jsx("div", { className: "grid grid-cols-7 gap-0.5", children: cells.map((cell, i) => {
                    if (!cell)
                        return _jsx("div", {}, i);
                    const { jd } = cell;
                    const isToday = todayJ.jy === view.jy && todayJ.jm === view.jm && todayJ.jd === jd;
                    const isSelected = !!sel && sel.jy === view.jy && sel.jm === view.jm && sel.jd === jd;
                    const isFriday = jalaliDayOfWeek(view.jy, view.jm, jd) === 6;
                    const disabled = isDisabled(view.jy, view.jm, jd);
                    const marked = isMarked(view.jy, view.jm, jd);
                    // Has a menu (only meaningful when availableSet is provided).
                    const hasMenu = !!availableSet && isAvailable(view.jy, view.jm, jd);
                    return (_jsxs(motion.button, { type: "button", disabled: disabled, whileTap: disabled ? {} : { scale: 0.92 }, onClick: () => onChange(formatJalali(view.jy, view.jm, jd)), className: clsx('relative aspect-square rounded-md text-xs font-medium transition', disabled && 'cursor-not-allowed text-gray-300 dark:text-gray-700', !disabled && !isSelected && 'text-gray-700 hover:bg-arshe-100 dark:text-gray-200 dark:hover:bg-arshe-900', 
                        // Visible highlight on days that have a menu, so they "pop".
                        hasMenu && !disabled && !isSelected && 'bg-arshe-50 ring-1 ring-arshe-200 dark:bg-arshe-950/60 dark:ring-arshe-800', isFriday && !disabled && 'text-red-600 dark:text-red-400', isToday && !isSelected && 'ring-2 ring-arshe-500', isSelected && 'bg-arshe-500 text-white shadow-sm hover:bg-arshe-600'), children: [toPersianDigits(jd), marked && !isSelected && (_jsx("span", { className: "absolute bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-arshe-500", "aria-hidden": true }))] }, i));
                }) }), availableSet && !currentMonthHasAvailable && (_jsxs("div", { className: "mt-2 flex items-center justify-between gap-2 rounded-lg bg-gray-50 px-2 py-1.5 text-[11px] text-gray-600 dark:bg-gray-800 dark:text-gray-300", children: [_jsx("span", { children: "\u0645\u0646\u0648\u06CC\u06CC \u062F\u0631 \u0627\u06CC\u0646 \u0645\u0627\u0647 \u0646\u06CC\u0633\u062A" }), (() => {
                        const next = findNextMonthWithAvailable(1);
                        if (!next)
                            return null;
                        return (_jsx("button", { type: "button", onClick: () => setView(next), className: "rounded-md bg-arshe-500 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-arshe-600", children: "\u0645\u0627\u0647 \u0628\u0639\u062F\u06CC \u0645\u0646\u0648\u062F\u0627\u0631 \u2190" }));
                    })()] })), _jsxs("div", { className: "mt-2 flex items-center justify-between text-[11px]", children: [_jsx("button", { type: "button", onClick: () => {
                            setView({ jy: todayJ.jy, jm: todayJ.jm });
                            if (!isDisabled(todayJ.jy, todayJ.jm, todayJ.jd)) {
                                onChange(formatJalali(todayJ.jy, todayJ.jm, todayJ.jd));
                            }
                        }, className: "rounded-md px-2 py-1 text-arshe-600 hover:bg-arshe-100 dark:hover:bg-arshe-950", children: "\u0627\u0645\u0631\u0648\u0632" }), sel && (_jsx("span", { className: "text-gray-500", children: toPersianDigits(`${sel.jy}/${String(sel.jm).padStart(2, '0')}/${String(sel.jd).padStart(2, '0')}`) }))] })] }));
}
